<script>
  import { onMount } from "svelte";
  import * as PropTypes from "prop-types";
  const { checkPropTypes, func, object } = PropTypes;
  import { getCommentsById } from "@/api";
  import SvgUp from "$assets/svg/arrow-up.svg?src";
  import SvgDown from "$assets/svg/chevron-down.svg?src";
  import SvgWrapper from "@/components/atoms/SvgWrapper.svelte";
  import InfiniteScroll from "@/components/molecules/InfiniteScroll.svelte";
  import Comment from "./comment.svelte";
  import Sort from "./sort.svelte";
  import * as css from "@/utils/css";
  import constants from "@/constants";

  export let item, handleCloseComment;

  const propTypes = {
    item: object,
    handleCloseComment: func,
  };

  const { DEFAULT_USER_PIC, NO_MORE_POSTS_OFFSET } = constants;
  const maxWordsInCaption = 6;
  const sortText = {
    trending: "Top Comments",
    time: "Newest First",
  };

  let commentsContainer;
  let comments = [];
  let offset = null;
  let sortMenu = false;
  let fullHeight = false;
  let hideSeeMore = false;
  let fetchingComments = false;
  let fetchingReplies = false;
  let hasMoreComments = true;
  let sortBy = "trending";
  const sortOrder = "descending";

  $: isSeeMoreRequired = item.c && item.c.split(" ").length > maxWordsInCaption;

  $: sliceWords =
    item.c && item.c.split(" ").slice(0, maxWordsInCaption).join(" ");

  $: caption = isSeeMoreRequired ? sliceWords : item.c;

  const handleClose = () => {
    fullHeight = false;
    setTimeout(handleCloseComment, 200);
  };

  const handleOpenSort = () => {
    sortMenu = true;
  };

  const handleCloseSort = () => {
    sortMenu = false;
  };

  const handleSelectSort = (s) => {
    sortBy = s;
    fetchComments("replace");
  };

  const handleSeeMore = (e) => {
    caption = item.c;
    hideSeeMore = true;
  };
  const handleSeeLess = (e) => {
    caption = sliceWords || "";
    hideSeeMore = false;
  };

  const handleReplyClick = async (comment, i) => {
    if (comment.replies.length <= 0) {
      await fetchReplies(comment, i);
    }
    comments[i].showReply = !comment.showReply;
  };

  const handleMoreReplies = async (comment, i) => {
    if (
      !comment.hasMoreReplies ||
      comment.replyCount - comment.replies.length <= 0
    )
      comments[i].showReply = false;
    await fetchReplies(comment, i);
  };

  const fetchReplies = async (comment, i, reducerAction = "concat") => {
    if (reducerAction === "replace" && !fetchingReplies) {
      comments[i].replies = [];
      comments[i].offset = null;
      comment.offset = null;
    }

    if (fetchingReplies || comment.offset === NO_MORE_POSTS_OFFSET) return;
    fetchingReplies = true;

    try {
      let {
        d,
        a,
        offset: newOffset,
      } = await getCommentsById(
        window.fetch,
        item.i,
        `${comment.c}`,
        comment.offset,
        "time",
        "ascending"
      );
      if (newOffset !== comment.offset) {
        d = d.map((c) => ({
          ...c,
          ah: a[c.a]?.h,
          ap: a[c.a]?.tu,
          an: a[c.a]?.n,
          badgeUrl: a[c.a]?.badgeUrl,
        }));
        comments[i].replies = [...comments[i].replies, ...d];
        comments[i].offset = newOffset;
      }
      if (!d || d.length <= 0) {
        comments[i].hasMoreReplies = false;
      }
    } catch (e) {
      console.warn(e);
    } finally {
      fetchingReplies = false;
    }
  };

  const fetchComments = async (reducerAction = "concat") => {
    if (reducerAction === "replace" && !fetchingComments) {
      comments = [];
      offset = null;
    }

    if (fetchingComments || offset === NO_MORE_POSTS_OFFSET) return;
    fetchingComments = true;

    try {
      let {
        d,
        a,
        offset: newOffset,
      } = await getCommentsById(
        window.fetch,
        item.i,
        null,
        offset,
        sortBy,
        sortOrder
      );
      if (newOffset !== offset) {
        d = d.map((c) => ({
          ...c,
          ah: a[c.a]?.h,
          ap: a[c.a]?.tu,
          an: a[c.a]?.n,
          showReply: false,
          replies: [],
          offset: null,
          hasMoreReplies: true,
          badgeUrl: a[c.a]?.badgeUrl,
        }));
        comments = [...comments, ...d];
        offset = newOffset;
      }
      if (!d || d.length <= 0) {
        hasMoreComments = false;
      }
    } catch (e) {
      console.warn(e);
    } finally {
      fetchingComments = false;
    }
  };

  onMount(() => {
    checkPropTypes(propTypes, $$props, "prop", "Comments");
    fetchComments("concat");
    setTimeout(() => {
      fullHeight = true;
    }, 0);
  });
</script>

<div
  data-testid="comment-section"
  on:touchstart|stopPropagation
  on:mousedown|stopPropagation
  on:mouseup|stopPropagation
  on:click={handleClose}
  class={`${css.fullCover} z-[2] bg-transparent flex items-center justify-end flex-col text-white`}
>
  {#if sortMenu}
    <Sort {item} {sortBy} {sortText} {handleSelectSort} {handleCloseSort} />
  {/if}
  <div
    data-testid="comments-container"
    bind:this={commentsContainer}
    on:click|stopPropagation
    class={`${
      fullHeight ? "translate-y-0 duration-[.5s]" : "translate-y-full"
    } h-3/4 w-full overflow-auto bg-black rounded-[15px] cursor-default transition-transform`}
  >
    <div class="flex p-md">
      <div class="my-sm relative">
        <a href={`/@${item.ath.h}?referrer=web`} class="flex w-2xl">
          <img
            src={item.ath.tu || DEFAULT_USER_PIC}
            class="rounded-[50%] h-2xl w-2xl bg-cover bg-light-grey object-cover lazyload"
            alt={item.ath.n}
            width="48"
            height="48"
          />
        </a>
      </div>
      <div class="my-md ml-md">
        <div class="flex items-center text-md font-extrabold">
          <a href={`/@${item.ath.h}?referrer=web`}>@{item.ath.h}</a>
          {#if item.ath.badgeUrl}
            <img class="h-md w-md ml-xxs" src={item.ath.badgeUrl} alt="Badge" />
          {/if}
        </div>

        {#if caption}
          {#if isSeeMoreRequired}
            <div>
              <span class="break-words">{caption}</span>
              <span
                on:mousedown|stopPropagation
                on:mouseup={hideSeeMore ? handleSeeLess : handleSeeMore}
                class={`font-bold`}
                >{hideSeeMore ? "...See Less" : "...See More"}</span
              >
            </div>
          {:else}
            <div class="break-words">{caption}</div>
          {/if}
        {/if}
      </div>
    </div>
    <hr class={css.hr} />
    <div
      on:click={handleOpenSort}
      class="inline-block text-[15px] md:text-[18px] text-secondary-text-moj mx-md cursor-pointer"
    >
      {sortText[sortBy]}
      <span class="relative top-1">
        {#if sortOrder === "ascending"}
          <SvgWrapper
            svg={SvgUp}
            className="h-mmd w-mmd text-secondary-text-moj inline-block"
          />
        {:else}
          <SvgWrapper
            svg={SvgDown}
            className="h-mmd w-mmd text-secondary-text-moj inline-block"
          />
        {/if}
      </span>
    </div>
    <div>
      {#each comments as comment, i (comment.c)}
        <Comment {comment} />
        {#if comment.replyCount}
          <div class="ml-5xxl">
            <div
              on:click={() => handleReplyClick(comment, i)}
              class="mx-md inline-block text-secondary-text-moj cursor-pointer"
            >
              <span class="tracking-[-2px]">-------</span>{" "}
              {comment.showReply
                ? "Hide replies"
                : `View ${comment.replyCount} repl${
                    comment.replyCount === 1 ? "y" : "ies"
                  }`}
            </div>
            {#if comment.showReply}
              {#each comment.replies as reply (reply.replyNumber)}
                <Comment comment={reply} />
              {/each}
              <div
                on:click={() => handleMoreReplies(comment, i)}
                class="mx-md inline-block text-secondary-text-moj"
              >
                <span class="tracking-[-2px]">-------</span>{" "}
                {comment.hasMoreReplies &&
                comment.replyCount - comment.replies.length > 0
                  ? `View ${
                      comment.replyCount - comment.replies.length
                    } more repl${comment.replyCount === 1 ? "y" : "ies"}`
                  : "Hide replies"}
              </div>
            {/if}
          </div>
        {/if}
      {/each}
    </div>
    {#if commentsContainer}
      <InfiniteScroll
        elementScroll={commentsContainer}
        threshold={500}
        isDarkTheme={true}
        hasMore={hasMoreComments}
        loading={fetchingComments}
        on:loadMore={() => fetchComments("concat")}
      />
    {/if}
  </div>
</div>
